<div (click)="openDrawer()">
    <ng-content></ng-content>
</div>

@let hasNoUnsavedChanges = !hasQFContextChanged() && !hasFormChanged();
<ca-drawer
    #drawer
    overriddenWidth="664px"
    [bodyNoPaddings]="true"
    [bodyBordered]="true"
    (visibleChange)="onVisibleChange($event)"
    [formGroup]="form"
    [closeConfirm]="{
        title: 'RBR.ROOM_EDITOR.DISCARD_CHANGES_DIALOG.TITLE' | translate,
        content: 'RBR.ROOM_EDITOR.DISCARD_CHANGES_DIALOG.CONTENT' | translate,
        cancelBtn: 'RBR.ROOM_EDITOR.DISCARD_CHANGES_DIALOG.CANCEL' | translate,
        confirmBtn: 'RBR.ROOM_EDITOR.DISCARD_CHANGES_DIALOG.CONFIRM' | translate,
        disabled: hasNoUnsavedChanges
    }"
    (closeCancelled)="onClose()"
>
    <!--
        # TICKET: https://viessmann.atlassian.net/browse/CAVP-3743

        # TODO: Refactor Drawer component to render projected content lazily inside Drawer itself.

        # MAIN PROBLEM: even if Drawer does not render projected content into DOM while hidden,
        Angular can still instantiate components from parent ng-content projection.
        This means heavy children (for example Question Flow) may run constructor/ngOnInit/effect
        before the drawer is visible.

        # CURRENT WORKAROUND: @if condition for each ng-content section - e.g. question flow is rendered
        only when drawer is open (visible).

        # AFFECTED COMPONENTS: Every component using ca-drawer
          - <add-rooms>,
          - <room-editor>,
          - <calculation-base>
          - <default-values>
          - <ui-components>

        # PROPOSED SOLUTION:
          1) Refactor Drawer to accept TemplateRef slots for header/body/footer instead of relying
              only on ng-content projection.
          2) Inside Drawer, instantiate slot embedded views only when visible() is true.
          3) Keep backward compatibility during migration: support old ng-content API as fallback
              while template slots are adopted by clients.
          4) Migrate heavy-content clients first (Room Editor, Product Picker, etc.) to template
              slots to remove local isDrawerVisible guards.
          5) After migration, remove fallback projection path to guarantee lazy instantiation by design.
    -->
    @if (isDrawerVisible()) {
        <!-- above @if was supposed to be the workaround, but does not render after changing to true -->
        <header drawer-header class="flex items-center gap-6">
            @if (isRoomNameEdited()) {
                <mat-form-field
                    appearance="outline"
                    subscriptSizing="dynamic"
                    [hideRequiredMarker]="true"
                    class="ca-form-field ca-form-field-xs"
                >
                    <input type="text" matInput formControlName="name" />
                </mat-form-field>
                <div class="flex gap-2 items-end">
                    <ca-icon
                        icon="check"
                        class="cursor-pointer ca-icon-s-32-p-0-f-32"
                        [class.ca-icon-disabled]="form.controls['name'].invalid"
                        (click)="onNameSave()"
                    ></ca-icon>
                    <ca-icon icon="cross" class="cursor-pointer ca-icon-cancel" (click)="onNameCancel()"></ca-icon>
                </div>
            } @else {
                <h4 class="ca-headline-4 ca-bold m-0">{{ form.controls['name'].value }}</h4>
                <ca-icon icon="pencil" class="cursor-pointer" (click)="onNameEdit()"></ca-icon>
            }
        </header>
    }
    @if (hasConfirmError()) {
        <div drawer-top class="flex items-center gap-6 py-4">
            <ca-message
                [message]="{
                    type: messageTypes.Error,
                    variant: 'solid',
                    title: 'RBR.ROOM_EDITOR.ERROR.TITLE' | translate,
                    content: 'RBR.ROOM_EDITOR.ERROR.CONTENT' | translate,
                    closable: true
                }"
                (remove)="hasConfirmError.set(false)"
            ></ca-message>
        </div>
    }
    @if (form && isDrawerVisible()) {
        <div drawer-body class="flex flex-col gap-4">
            @if (isConfirmPending() && innerLoading()) {
                <div class="ca-fullscreen-spinner-container">
                    <ca-spinner
                        data-testid="saving-room-spinner"
                        [diameter]="80"
                        [text]="'RBR.ROOM_EDITOR.SAVING' | translate"
                        [subtext]="'RBR.ROOM_EDITOR.SAVING_DESCRIPTION' | translate"
                    ></ca-spinner>
                </div>
            }
            <div>
                <ca-question-flow
                    #questionFlowCmp
                    [initialContextModel]="questionFlowContext()"
                    [hideActionButtons]="true"
                    [hideTimeline]="true"
                    [configuration]="qfEditRoomMock"
                    (contextChange)="onContextChange($event)"
                    (validityChange)="onValidityChange($event)"
                ></ca-question-flow>
            </div>
        </div>
    }
    @if (isDrawerVisible()) {
        <div drawer-footer class="flex justify-between items-center w-full">
            <button
                mat-button
                class="ca-button ca-button-m"
                [disabled]="isConfirmPending() && innerLoading()"
                (click)="tryToClose()"
                [caConfirm]="{
                    title: 'RBR.ROOM_EDITOR.DISCARD_CHANGES_DIALOG.TITLE' | translate,
                    content: 'RBR.ROOM_EDITOR.DISCARD_CHANGES_DIALOG.CONTENT' | translate,
                    cancelBtn: 'RBR.ROOM_EDITOR.DISCARD_CHANGES_DIALOG.CANCEL' | translate,
                    confirmBtn: 'RBR.ROOM_EDITOR.DISCARD_CHANGES_DIALOG.CONFIRM' | translate,
                    disabled: hasNoUnsavedChanges
                }"
                (cancel)="onClose()"
            >
                <span>{{ 'CA.BUTTON.CANCEL' | translate }}</span>
            </button>
            <button
                mat-flat-button
                class="ca-button ca-button-m"
                [disabled]="
                    !form ||
                    form.invalid ||
                    !isQuestionFlowValid() ||
                    (!hasQFContextChanged() && !hasFormChanged()) ||
                    (isConfirmPending() && innerLoading())
                "
                (click)="onConfirm()"
            >
                <span>{{ 'CA.BUTTON.CONFIRM' | translate }}</span>
            </button>
        </div>
    }
</ca-drawer>
